/*
  <static-pdp-carousel>
  Generic single-slide-visible carousel used by the Static PDP template
  (currently: the review slider under the add-to-cart button).

  Markup contract (all attributes, so this element can be reused anywhere):
    [data-carousel-viewport]  - the clipping/overflow wrapper
    [data-carousel-track]     - the flex track that gets translateX'd
    [data-carousel-slide]     - each slide (flex: 0 0 100%)
    [data-carousel-prev]      - previous button (optional)
    [data-carousel-next]      - next button (optional)
    [data-carousel-dot]       - pagination dot buttons (optional)

  Behaviour: prev/next buttons, dot navigation, and touch swipe. Loops
  (wraps around) at the ends rather than disabling the arrows.
*/
if (!customElements.get('static-pdp-carousel')) {
  customElements.define(
    'static-pdp-carousel',
    class StaticPdpCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        this.viewport = this.querySelector('[data-carousel-viewport]');
        this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
        this.prevBtn = this.querySelector('[data-carousel-prev]');
        this.nextBtn = this.querySelector('[data-carousel-next]');
        this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));

        this.index = 0;
        this.total = this.slides.length;

        if (this.total <= 1) {
          if (this.prevBtn) this.prevBtn.hidden = true;
          if (this.nextBtn) this.nextBtn.hidden = true;
          return;
        }

        this.prevBtn?.addEventListener('click', () => this.go(this.index - 1));
        this.nextBtn?.addEventListener('click', () => this.go(this.index + 1));
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

        this.bindSwipe();
        this.render();
      }

      go(newIndex) {
        this.index = (newIndex + this.total) % this.total;
        this.render();
      }

      render() {
        if (!this.track) return;
        this.track.style.transform = `translateX(-${this.index * 100}%)`;
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
      }

      bindSwipe() {
        if (!this.viewport) return;

        let startX = 0;
        let currentX = 0;
        let dragging = false;
        const threshold = 40;

        const onStart = (x) => {
          dragging = true;
          startX = x;
          currentX = x;
          this.track.style.transition = 'none';
        };

        const onMove = (x) => {
          if (!dragging) return;
          currentX = x;
          const delta = currentX - startX;
          const percentDelta = (delta / this.viewport.clientWidth) * 100;
          this.track.style.transform = `translateX(calc(-${this.index * 100}% + ${percentDelta}%))`;
        };

        const onEnd = () => {
          if (!dragging) return;
          dragging = false;
          this.track.style.transition = '';
          const delta = currentX - startX;

          if (Math.abs(delta) > threshold) {
            if (delta < 0) this.go(this.index + 1);
            else this.go(this.index - 1);
          } else {
            this.render();
          }
        };

        this.viewport.addEventListener('touchstart', (e) => onStart(e.touches[0].clientX), {
          passive: true,
        });
        this.viewport.addEventListener('touchmove', (e) => onMove(e.touches[0].clientX), {
          passive: true,
        });
        this.viewport.addEventListener('touchend', onEnd);
      }
    }
  );
}
